import React, { useMemo, useRef, useEffect } from 'react';
import * as constantsViewer from '../../constants';
import { compute3DMeshDataAndPosition, loadTexture } from '../../utils';
import {
  createReactThreeGeometry,
  createReactThreeMaterial,
} from '../../reactThreeUtils';
import { getMaterialData } from '../../helpers';
import { useSpring, a } from 'react-spring/three';
import textureCornerInox from 'assets/images/textures/alu-inox.jpg';
import textureCornerGalva from 'assets/images/textures/galva.jpg';
import data from './data/corner';

export function Corner({
  corner,
  cabinetWidth = 1000,
  cabinetDepth = 420,
  unselected = false,
}) {
  const cornerPosition = corner.position;

  const mesh = useRef();

  const baseData = useMemo(() => compute3DMeshDataAndPosition(data), [data]);

  const textures = useMemo(
    () => ({
      textureInoxLoaded: loadTexture(textureCornerInox),
      textureGalvaLoaded: loadTexture(textureCornerGalva),
    }),
    [textureCornerInox, textureCornerGalva],
  );

  const materialData = getMaterialData(corner, textures);

  const [spring, set] = useSpring(() => ({
    position: getPosition(
      baseData.position,
      baseData.size,
      cabinetWidth,
      cabinetDepth,
      cornerPosition,
    ),
    immediate: !constantsViewer.REACT_SPRING_ANIMATIONS_ACTIVE,
    config: constantsViewer.REACT_SPRING_ANIMATIONS_CONF,
  }));

  useEffect(() => {
    let self = mesh.current;
    if (self) {
      set({
        position: getPosition(
          baseData.position,
          baseData.size,
          cabinetWidth,
          cabinetDepth,
          cornerPosition,
        ),
      });
    }
  }, [cabinetWidth, cabinetDepth]);

  return (
    <a.mesh
      ref={mesh}
      visible={true}
      renderOrder={constantsViewer.ZORDER_CORNER}
      receiveShadow
      castShadow
      position={spring.position}
      rotation={getRotation(cornerPosition)}
    >
      {createReactThreeGeometry(baseData)}
      {createReactThreeMaterial(materialData, unselected)}
    </a.mesh>
  );
}

function getRotation(cornerPosition) {
  switch (cornerPosition) {
    case 'FL':
      return [0, 0, 0];

    case 'FR':
      return [0, Math.PI / 2, 0];

    case 'BL':
      return [0, -Math.PI / 2, 0];

    case 'BR':
      return [0, Math.PI, 0];

    default:
      return null;
  }
}

function getPosition(
  basePosition,
  size,
  cabinetWidth,
  cabinetDepth,
  cornerPosition,
) {
  const cabinetDiffWith = cabinetWidth - constantsViewer.MODEL_CABINET_WIDTH;
  const cabinetDiffDepth = cabinetDepth - constantsViewer.MODEL_CABINET_DEPTH;

  switch (cornerPosition) {
    case 'FL':
      return [
        basePosition.x - cabinetDiffWith / 2,
        basePosition.y,
        basePosition.z + cabinetDiffDepth / 2,
      ];

    case 'FR':
      return [
        basePosition.x +
          constantsViewer.MODEL_CABINET_WIDTH +
          cabinetDiffWith / 2 -
          size.x -
          5,
        basePosition.y,
        basePosition.z + cabinetDiffDepth / 2,
      ];

    case 'BL':
      return [
        basePosition.x - cabinetDiffWith / 2,
        basePosition.y,
        basePosition.z + cabinetDiffDepth / 2 - cabinetDepth + size.z,
      ];

    case 'BR':
      return [
        basePosition.x +
          constantsViewer.MODEL_CABINET_WIDTH +
          cabinetDiffWith / 2 -
          size.x -
          5,
        basePosition.y,
        basePosition.z + cabinetDiffDepth / 2 - cabinetDepth + size.z,
      ];

    default:
      return null;
  }
}
